import { useMemo, useState, type FormEvent } from "react";
import type {
  AdminLocation,
  AdminProvider,
  BlockInput,
  ScheduleBlock,
  ScheduleShift,
  ShiftInput,
} from "../api/types.js";
import { Card } from "./Card.js";
import { Field } from "./Field.js";
import { Select } from "./Select.js";
import { ErrorBanner } from "./ErrorBanner.js";
import { expandWeekly, MAX_REPEAT_WEEKS } from "../lib/schedule-repeat.js";
import { findSecondLocationConflict, locationsForProvider } from "../lib/schedule-guards.js";

// A drag-select opens this in "create" mode (provider/date/time come from
// the selection, entry type is a choice); clicking an existing shift or
// block opens it pre-filled in the matching "edit-*" mode (entry type
// fixed, Delete available). SchedulePage owns this state — see its
// `popover` field.
export type ShiftPopoverState =
  | {
      kind: "create";
      providerId: string;
      date: string;
      startTime: string;
      endTime: string;
      allDay: boolean;
    }
  | { kind: "edit-shift"; shift: ScheduleShift }
  | { kind: "edit-block"; block: ScheduleBlock };

type Props = {
  state: ShiftPopoverState;
  providers: readonly AdminProvider[];
  locations: readonly AdminLocation[];
  // The currently loaded shifts for the visible range — used only for the
  // one-physical-location-per-day inline conflict check (behavior 2).
  shifts: readonly ScheduleShift[];
  locationById: ReadonlyMap<string, AdminLocation>;
  saving: boolean;
  deleting: boolean;
  error: string | null;
  // `repeatUntil` (create mode only): create weekly copies through this date.
  onSaveShift: (input: ShiftInput, id?: string, repeatUntil?: string) => void;
  onSaveBlock: (input: BlockInput, id?: string) => void;
  onDeleteShift: (id: string) => void;
  onDeleteBlock: (id: string) => void;
  onClose: () => void;
};

function dateOf(state: ShiftPopoverState): string {
  if (state.kind === "create") return state.date;
  if (state.kind === "edit-shift") return state.shift.shift_date;
  return state.block.block_date;
}

function providerIdOf(state: ShiftPopoverState): string {
  if (state.kind === "create") return state.providerId;
  if (state.kind === "edit-shift") return state.shift.provider_id;
  return state.block.provider_id;
}

export function ShiftPopover({
  state,
  providers,
  locations,
  shifts,
  locationById,
  saving,
  deleting,
  error,
  onSaveShift,
  onSaveBlock,
  onDeleteShift,
  onDeleteBlock,
  onClose,
}: Props) {
  const isCreate = state.kind === "create";
  const [entryType, setEntryType] = useState<"shift" | "block">(
    state.kind === "edit-block" ? "block" : "shift",
  );
  const [locationId, setLocationId] = useState(
    state.kind === "edit-shift" ? state.shift.location_id : "",
  );
  const [label, setLabel] = useState(
    state.kind === "edit-block" ? (state.block.label ?? "") : "",
  );
  const [allDay, setAllDay] = useState(
    state.kind === "create"
      ? state.allDay
      : state.kind === "edit-shift"
        ? state.shift.all_day
        : state.block.all_day,
  );
  const [startTime, setStartTime] = useState(
    state.kind === "create"
      ? state.startTime
      : state.kind === "edit-shift"
        ? state.shift.start_time
        : state.block.start_time,
  );
  const [endTime, setEndTime] = useState(
    state.kind === "create"
      ? state.endTime
      : state.kind === "edit-shift"
        ? state.shift.end_time
        : state.block.end_time,
  );

  const [repeatUntil, setRepeatUntil] = useState("");

  const date = dateOf(state);
  const providerId = providerIdOf(state);
  const provider = providers.find((p) => p.id === providerId);
  const validLocations = useMemo(
    () => locationsForProvider(locations, provider),
    [locations, provider],
  );

  const conflict =
    entryType === "shift" && locationId
      ? findSecondLocationConflict(shifts, locationById, {
          providerId,
          date,
          locationId,
          ...(state.kind === "edit-shift" ? { excludeShiftId: state.shift.id } : {}),
        })
      : null;

  const repeatActive = isCreate && entryType === "shift" && repeatUntil.length > 0;
  let repeatError: string | null = null;
  if (repeatActive) {
    if (repeatUntil < date) {
      repeatError = `Must be on or after ${date}`;
    } else {
      try {
        expandWeekly(date, repeatUntil);
      } catch {
        repeatError = `Repeat can span at most ${MAX_REPEAT_WEEKS} weeks`;
      }
    }
  }

  const timesValid =
    allDay || (startTime.length > 0 && endTime.length > 0 && startTime < endTime);
  const canSave =
    !saving &&
    timesValid &&
    (entryType === "block" ||
      (locationId.length > 0 && !conflict && !repeatError));

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    if (entryType === "shift") {
      const input: ShiftInput = {
        provider_id: providerId,
        location_id: locationId,
        shift_date: date,
        start_time: startTime || "00:00",
        end_time: endTime || "23:59",
        all_day: allDay,
      };
      const id = state.kind === "edit-shift" ? state.shift.id : undefined;
      if (repeatActive) onSaveShift(input, id, repeatUntil);
      else onSaveShift(input, id);
    } else {
      const input: BlockInput = {
        provider_id: providerId,
        block_date: date,
        start_time: allDay ? "00:00" : startTime,
        end_time: allDay ? "23:59" : endTime,
        all_day: allDay,
        label: label.trim() ? label.trim() : null,
      };
      onSaveBlock(input, state.kind === "edit-block" ? state.block.id : undefined);
    }
  }

  function handleDelete() {
    if (state.kind === "edit-shift") onDeleteShift(state.shift.id);
    else if (state.kind === "edit-block") onDeleteBlock(state.block.id);
  }

  const title =
    state.kind === "create"
      ? "New schedule entry"
      : state.kind === "edit-shift"
        ? "Edit shift"
        : "Edit block";

  return (
    <div
      role="dialog"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <Card title={title} className="w-full max-w-md">
        <p className="mb-3 text-sm text-slate-600">{date}</p>
        {error ? <ErrorBanner message={error} /> : null}
        <form onSubmit={handleSubmit} noValidate className="grid gap-4">
          {isCreate ? (
            <fieldset className="grid gap-2">
              <legend className="text-sm font-medium text-slate-700">
                Entry type
              </legend>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="entry-type"
                  value="shift"
                  checked={entryType === "shift"}
                  onChange={() => setEntryType("shift")}
                />
                Shift at…
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="entry-type"
                  value="block"
                  checked={entryType === "block"}
                  onChange={() => setEntryType("block")}
                />
                Block off
              </label>
            </fieldset>
          ) : null}

          {entryType === "shift" ? (
            <div>
              <Select
                label="Location"
                required
                placeholder="Choose a location"
                options={validLocations.map((l) => ({
                  value: l.id,
                  label: l.display_name,
                }))}
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              />
              {conflict ? (
                <p className="mt-1 text-xs text-red-600">
                  Already at {conflict.display_name} that day
                </p>
              ) : null}
            </div>
          ) : (
            <Field
              label="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Lunch, PTO, etc."
            />
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            All day
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Start time"
              type="time"
              required
              disabled={allDay}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Field
              label="End time"
              type="time"
              required
              disabled={allDay}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          {isCreate && entryType === "shift" ? (
            <Field
              label="Repeat weekly until"
              type="date"
              min={date}
              value={repeatUntil}
              error={repeatError ?? undefined}
              onChange={(e) => setRepeatUntil(e.target.value)}
            />
          ) : null}

          <div className="flex items-center justify-between gap-2">
            <div>
              {!isCreate ? (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="rounded-md border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSave}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
